import React, { useEffect, useState } from 'react';
import { Box, VStack, Text, Card, Flex, Separator } from '@chakra-ui/react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { IoArrowBack, IoShareOutline } from 'react-icons/io5';
import { FaSafari, FaChrome } from 'react-icons/fa';
import { MdPhoneIphone, MdAndroid, MdComputer, MdOutlineInstallDesktop } from 'react-icons/md';

type DeviceType = 'ios-safari' | 'android-chrome' | 'pc-chrome' | 'other';

const PWAInstallPage = () => {
  const navigate = useNavigate();
  const [deviceType, setDeviceType] = useState<DeviceType>('other');

  useEffect(() => {
    const detectDevice = (): DeviceType => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipad|ipod/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
      const isChrome = /chrome/.test(userAgent);

      if (isIOS && isSafari) {
        return 'ios-safari';
      } else if (isAndroid && isChrome) {
        return 'android-chrome';
      } else if (!isIOS && !isAndroid && isChrome) {
        return 'pc-chrome';
      }
      return 'other';
    };

    setDeviceType(detectDevice());
  }, []);

  const renderInstallInstructions = () => {
    switch (deviceType) {
      case 'ios-safari':
        return <IOSSafariInstructions />;
      case 'android-chrome':
        return <AndroidChromeInstructions />;
      case 'pc-chrome':
        return <PCChromeInstructions />;
      default:
        return <OtherDeviceInstructions />;
    }
  };

  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'ios-safari':
        return (
          <Flex align="center" gap={2}>
            <MdPhoneIphone size={24} />
            <FaSafari size={20} />
          </Flex>
        );
      case 'android-chrome':
        return (
          <Flex align="center" gap={2}>
            <MdAndroid size={24} />
            <FaChrome size={20} />
          </Flex>
        );
      case 'pc-chrome':
        return (
          <Flex align="center" gap={2}>
            <MdComputer size={24} />
            <FaChrome size={20} />
          </Flex>
        );
      default:
        return null;
    }
  };

  const getDeviceName = () => {
    switch (deviceType) {
      case 'ios-safari':
        return 'iPhone / iPad (Safari)';
      case 'android-chrome':
        return 'Android (Chrome)';
      case 'pc-chrome':
        return 'PC (Chrome)';
      default:
        return 'その他のデバイス';
    }
  };

  return (
    <Box w="full" h="100%" pt={16} pb={4} px={2} userSelect="none">
      <VStack w="full" h="full" gap={4} align="stretch" maxW="600px" mx="auto">
        {/* 戻るボタン */}
        <Button variant="ghost" size="sm" alignSelf="flex-start" onClick={() => navigate(-1)}>
          <IoArrowBack style={{ marginRight: '0.5rem' }} />
          戻る
        </Button>

        {/* ヘッダー */}
        <VStack gap={2} mb={2}>
          <Text fontSize="2xl" fontWeight="bold" color="teal.700">
            PWAのインストール方法
          </Text>
          <Flex align="center" gap={2} color="teal.600">
            {getDeviceIcon()}
            <Text fontSize="sm" fontWeight="medium">
              {getDeviceName()}
            </Text>
          </Flex>
        </VStack>

        {/* インストール手順 */}
        <Box flex={1} overflowY="auto" px={2} pt={1} pb={4}>
          {renderInstallInstructions()}
        </Box>
      </VStack>
    </Box>
  );
};

// iOS Safari用の手順
const IOSSafariInstructions = () => (
  <VStack align="stretch" gap={4}>
    <Card.Root variant="elevated" bg="white/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={4}>
          <Text fontSize="lg" fontWeight="bold" color="teal.700">
            📱 iPhoneでのインストール手順
          </Text>
          <Separator />
          <VStack align="stretch" gap={3}>
            <InstructionStep
              number={1}
              text={
                <>
                  画面下部の共有ボタン（
                  <IoShareOutline style={{ display: 'inline', verticalAlign: 'middle' }} />
                  ）をタップします
                </>
              }
            />
            <InstructionStep number={2} text="メニューから「ホーム画面に追加」をタップします" />
            <InstructionStep number={3} text="アプリ名を確認し、右上の「追加」をタップします" />
            <InstructionStep number={4} text="ホーム画面にBunPayのアイコンが追加されます" />
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>

    <Card.Root variant="elevated" bg="blue.50/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm" fontWeight="bold" color="blue.700">
            💡 ポイント
          </Text>
          <Text fontSize="sm" color="blue.900" lineHeight="1.6">
            ホーム画面から起動することで、アプリのように全画面で利用できます。ブラウザのアドレスバーが表示されないため、より快適に使用できます。
          </Text>
        </VStack>
      </Card.Body>
    </Card.Root>
  </VStack>
);

// Android Chrome用の手順
const AndroidChromeInstructions = () => (
  <VStack align="stretch" gap={4}>
    <Card.Root variant="elevated" bg="white/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={4}>
          <Text fontSize="lg" fontWeight="bold" color="teal.700">
            📱 Androidでのインストール手順
          </Text>
          <Separator />
          <VStack align="stretch" gap={3}>
            <InstructionStep number={1} text="画面右上のメニューボタン（⋮）をタップします" />
            <InstructionStep
              number={2}
              text="メニューから「アプリをインストール」または「ホーム画面に追加」をタップします"
            />
            <InstructionStep number={3} text="確認ダイアログで「インストール」をタップします" />
            <InstructionStep
              number={4}
              text="ホーム画面またはアプリ一覧にBunPayのアイコンが追加されます"
            />
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>

    <Card.Root variant="elevated" bg="blue.50/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm" fontWeight="bold" color="blue.700">
            💡 ポイント
          </Text>
          <Text fontSize="sm" color="blue.900" lineHeight="1.6">
            インストール後は、通常のアプリと同じようにホーム画面から起動できます。オフラインでも一部の機能が利用可能になります。
          </Text>
        </VStack>
      </Card.Body>
    </Card.Root>
  </VStack>
);

// PC Chrome用の手順
const PCChromeInstructions = () => (
  <VStack align="stretch" gap={4}>
    <Card.Root variant="elevated" bg="white/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={4}>
          <Text fontSize="lg" fontWeight="bold" color="teal.700">
            💻 PCでのインストール手順
          </Text>
          <Separator />
          <VStack align="stretch" gap={3}>
            <InstructionStep
              number={1}
              text={
                <>
                  アドレスバー右側のインストールアイコン（
                  <MdOutlineInstallDesktop style={{ display: 'inline', verticalAlign: 'middle' }} />
                  ）をクリックします
                </>
              }
            />
            <InstructionStep
              number={2}
              text="表示されるポップアップで「インストール」をクリックします"
            />
            <InstructionStep number={3} text="BunPayがデスクトップアプリとして起動します" />
            <InstructionStep
              number={4}
              text="次回からはデスクトップやタスクバーから直接起動できます"
            />
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>

    <Card.Root variant="elevated" bg="blue.50/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm" fontWeight="bold" color="blue.700">
            💡 ポイント
          </Text>
          <Text fontSize="sm" color="blue.900" lineHeight="1.6">
            PCにインストールすると、独立したウィンドウで起動し、タスクバーにピン留めすることも可能です。通知機能も利用できるようになります。
          </Text>
        </VStack>
      </Card.Body>
    </Card.Root>
  </VStack>
);

// その他のデバイス用
const OtherDeviceInstructions = () => (
  <VStack align="stretch" gap={4}>
    <Card.Root variant="elevated" bg="white/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={4}>
          <Text fontSize="lg" fontWeight="bold" color="teal.700">
            PWAについて
          </Text>
          <Separator />
          <Text fontSize="sm" color="gray.700" lineHeight="1.8">
            BunPayはPWA（Progressive Web
            App）として提供されています。PWAをインストールすると、アプリのように使用することができます。
          </Text>
          <VStack align="stretch" gap={2} mt={2}>
            <Text fontSize="sm" fontWeight="bold" color="gray.800">
              対応ブラウザ：
            </Text>
            <Text fontSize="sm" color="gray.700">
              • iPhone/iPad: Safari
            </Text>
            <Text fontSize="sm" color="gray.700">
              • Android: Chrome
            </Text>
            <Text fontSize="sm" color="gray.700">
              • PC: Chrome, Edge
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>

    <Card.Root variant="elevated" bg="yellow.50/80" borderRadius="2xl">
      <Card.Body>
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm" fontWeight="bold" color="yellow.800">
            ⚠️ お知らせ
          </Text>
          <Text fontSize="sm" color="yellow.900" lineHeight="1.6">
            お使いのブラウザまたはデバイスでは、PWAのインストールがサポートされていない可能性があります。対応ブラウザをご利用ください。
          </Text>
        </VStack>
      </Card.Body>
    </Card.Root>
  </VStack>
);

// 手順のステップコンポーネント
const InstructionStep = ({ number, text }: { number: number; text: React.ReactNode }) => (
  <Flex gap={3} align="flex-start">
    <Flex
      align="center"
      justify="center"
      w={8}
      h={8}
      borderRadius="full"
      bg="teal.500"
      color="white"
      fontWeight="bold"
      fontSize="sm"
      flexShrink={0}
    >
      {number}
    </Flex>
    <Text fontSize="sm" color="gray.700" lineHeight="1.8" pt={1}>
      {text}
    </Text>
  </Flex>
);

export default PWAInstallPage;
